import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';

interface StatCard {
  title: string;
  value: number;
  color: string;
}

interface Project {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  ward?: string;
}

const getConstituencyForWard = (wardRaw?: string): string => {
  if (!wardRaw) return 'County wide';
  const w = wardRaw.toLowerCase();

  // Changamwe Constituency
  if (
    w.includes('port reitz') || w.includes('portreiz') || w.includes('portriz') ||
    w.includes('kipevu') ||
    w.includes('airport') ||
    w.includes('changamwe') ||
    w.includes('chaani')
  ) {
    return 'Changamwe';
  }

  // Jomvu Constituency
  if (
    w.includes('jomvu kuu') || w.includes('jomvu ward') || w === 'jomvu' ||
    w.includes('miritini') ||
    w.includes('mikindani')
  ) {
    return 'Jomvu';
  }

  // Kisauni Constituency
  if (
    w.includes('mjambere') ||
    w.includes('junda') ||
    w.includes('bamburi') ||
    w.includes('mwakirunge') ||
    w.includes('mtopanga') ||
    w.includes('magogoni') ||
    w.includes('shanzu')
  ) {
    return 'Kisauni';
  }

  // Nyali Constituency
  if (
    w.includes('frere town') || w.includes('freretown') ||
    w.includes("ziwa la ng'ombe") || w.includes('ziwa la ngom') ||
    w.includes('mkomani') ||
    w.includes('kongowea') ||
    w.includes('kadzandani')
  ) {
    return 'Nyali';
  }

  // Likoni Constituency
  if (
    w.includes('mtongwe') ||
    w.includes('shika adabu') ||
    w.includes('bofu') ||
    w.includes('likoni') ||
    w.includes('timbwani')
  ) {
    return 'Likoni';
  }

  // Mvita Constituency
  if (
    w.includes('tudor') ||
    w.includes('mji wa kale') || w.includes('makadara') || w.includes('old town') ||
    w.includes('tononoka') ||
    w.includes('shimanzi') || w.includes('ganjoni') ||
    w.includes('majengo') ||
    w.includes('mvita')
  ) {
    return 'Mvita';
  }

  return 'County wide';
};

const normalizeProjectStatus = (rawStatus?: string): 'planned' | 'ongoing' | 'completed' | 'delayed' => {
  const value = (rawStatus || '').trim().toLowerCase();

  if (!value || value === 'nil') {
    return 'planned';
  }

  if (
    value.includes('not started') ||
    value.includes('has not taken off') ||
    value.includes('awaiting') ||
    value.includes('not been commissioned') ||
    value.includes('at inception')
  ) {
    return 'planned';
  }

  if (value.includes('delay')) {
    return 'delayed';
  }

  const percentageMatch = value.match(/(\d+(?:\.\d+)?)\s*%/);
  if (percentageMatch) {
    const pct = parseFloat(percentageMatch[1]);
    if (!Number.isNaN(pct)) {
      if (pct >= 100) return 'completed';
      if (pct > 0) return 'ongoing';
      return 'planned';
    }
  }

  if (value.includes('complete')) {
    return 'completed';
  }

  if (value.includes('ongoing') || value.includes('progress')) {
    return 'ongoing';
  }

  return 'ongoing';
};

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<StatCard[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all projects with pagination (get larger set for better data)
        const response = await apiService.getProjects(1, 1000);
        const projects: Project[] = response.data || [];
        const total = response.pagination.total;

        // Calculate status counts
        const statusCounts = {
          total: total,
          planned: 0,
          ongoing: 0,
          completed: 0,
          delayed: 0,
        };

        projects.forEach((project: Project) => {
          const key = normalizeProjectStatus(project.status);
          statusCounts[key]++;
        });

        // Set stats from real data
        setStats([
          { title: 'Total Projects', value: statusCounts.total, color: '#1976d2' },
          { title: 'Ongoing', value: statusCounts.ongoing, color: '#ff9800' },
          { title: 'Completed', value: statusCounts.completed, color: '#4caf50' },
          { title: 'Delayed', value: statusCounts.delayed, color: '#f44336' },
        ]);

        // Generate chart data based on projects per constituency (no reliance on dates)
        const constituencyCounts: { [key: string]: number } = {};
        projects.forEach((project: Project) => {
          const constituency = getConstituencyForWard(project.ward);
          constituencyCounts[constituency] = (constituencyCounts[constituency] || 0) + 1;
        });

        const constituencyChartData = Object.entries(constituencyCounts).map(([name, count]) => ({
          name,
          projects: count,
        }));

        setChartData(constituencyChartData);
      } catch (err: any) {
        console.error('Failed to fetch dashboard data:', err);
        setError(err.response?.data?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Welcome, {user?.firstName}!
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Grid container justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)', borderLeft: `4px solid ${stat.color}` }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" sx={{ color: stat.color, fontWeight: 'bold' }}>
                      {stat.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Charts */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Projects by Constituency
                </Typography>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" interval={0} angle={-30} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="projects" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                    No data available
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Project Status Distribution
                </Typography>
                {stats.length > 0 && stats.slice(1).some(s => s.value > 0) ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stats.slice(1)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ title, value }) => `${title}: ${value}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {stats.slice(1).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                    No project status data available
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default DashboardPage;
